/**
 * Incasso Phase Configuration
 *
 * Single source of truth for incasso kanban phase column definitions.
 * Defines visual appearance and properties for each of the 7 phases.
 */

import { IncassoPhase } from '../shared/incassoTypes';
import { KanbanColumn } from 'shared/components/Kanban/types';

export type IncassoPhaseColumn = KanbanColumn<IncassoPhase>;

/**
 * Incasso Phase Columns
 * Defines all 7 phase columns with their styling.
 */
export const incassoPhaseColumns: IncassoPhaseColumn[] = [
  {
    id: IncassoPhase.GEEN_CONTACT,
    label: 'Geen contact',
    color: 'text-slate-700',
    bgColor: 'bg-slate-50/50',
    headerBgColor: 'bg-slate-50',
    borderColor: 'border-slate-300',
  },
  {
    id: IncassoPhase.NIET_BEREIKBAAR_OF_ONDUIDELIJK,
    label: 'Niet bereikbaar / onduidelijk',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50/50',
    headerBgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  {
    id: IncassoPhase.MINNELIJKE_REGELING,
    label: 'Minnelijke regeling (gaat betalen of afbetalingsplan)',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50/50',
    headerBgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    id: IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN,
    label: 'Juridische procedure op te starten',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50/50',
    headerBgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
  },
  {
    id: IncassoPhase.JURIDISCHE_PROCEDURE_LOPEND,
    label: 'Juridische procedure lopende',
    color: 'text-red-700',
    bgColor: 'bg-red-50/50',
    headerBgColor: 'bg-red-50',
    borderColor: 'border-red-300',
  },
  {
    id: IncassoPhase.UITHUISZETTING,
    label: 'Uithuiszetting',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50/50',
    headerBgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
  },
  {
    id: IncassoPhase.INCASSO_VIA_DERDE,
    label: 'Incasso via derde of deurwaarder / loonbeslag',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50/50',
    headerBgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
  },
];

/**
 * Columns hidden by default. Users can toggle visibility.
 */
export const DEFAULT_HIDDEN_PHASES: IncassoPhase[] = [];

/**
 * Phases where snooze is allowed (all 7 phases).
 */
export const SNOOZE_ALLOWED_PHASES: IncassoPhase[] = [
  IncassoPhase.GEEN_CONTACT,
  IncassoPhase.NIET_BEREIKBAAR_OF_ONDUIDELIJK,
  IncassoPhase.MINNELIJKE_REGELING,
  IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN,
  IncassoPhase.JURIDISCHE_PROCEDURE_LOPEND,
  IncassoPhase.UITHUISZETTING,
  IncassoPhase.INCASSO_VIA_DERDE,
];

/**
 * Phase order map for backward-move detection.
 * Lower number = earlier in the flow.
 */
export const PHASE_ORDER: Record<IncassoPhase, number> = {
  [IncassoPhase.GEEN_CONTACT]: 0,
  [IncassoPhase.NIET_BEREIKBAAR_OF_ONDUIDELIJK]: 1,
  [IncassoPhase.MINNELIJKE_REGELING]: 2,
  [IncassoPhase.JURIDISCHE_PROCEDURE_OP_TE_STARTEN]: 3,
  [IncassoPhase.JURIDISCHE_PROCEDURE_LOPEND]: 4,
  [IncassoPhase.UITHUISZETTING]: 5,
  [IncassoPhase.INCASSO_VIA_DERDE]: 6,
};

/**
 * Helper to get the Dutch label for a phase.
 */
export const getPhaseLabel = (phase: IncassoPhase): string => {
  const column = incassoPhaseColumns.find((col) => col.id === phase);
  return column?.label ?? phase;
};

/**
 * Returns true when moving from fromPhase to toPhase is a backward move.
 */
export const isBackwardMove = (
  fromPhase: IncassoPhase,
  toPhase: IncassoPhase,
): boolean => {
  return PHASE_ORDER[toPhase] < PHASE_ORDER[fromPhase];
};
